import React, { useEffect, useMemo, useState } from 'react';
import {
  Routes as RRDRoutes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from 'react-router-dom';
import { usePermissions, hasPermission } from '@billda/permissions-ui';
import { PageHeader } from '@billda/design-system';
import { DEFAULT_AREA, visibleAreas } from './settings/settingsAreas';
import { GeneralArea } from './settings/GeneralArea';
import { OrganisationArea } from './settings/OrganisationArea';
import { SecurityArea } from './settings/SecurityArea';
import { AutoSubmitArea } from '../../features/organisation/components/AutoSubmitArea';
import { LateSubmissionArea } from '../../features/organisation/components/LateSubmissionArea';

/**
 * MUI-free media query (md breakpoint) — mirrors @mui/material useMediaQuery
 * semantics, matching the shared useSwipeToOpenSidebar pattern.
 */
function useMediaQueryMatches(query: string): boolean {
  const [matches, setMatches] = useState(
    () => window.matchMedia(query).matches
  );

  useEffect(() => {
    const mediaQueryList = window.matchMedia(query);
    const handleChange = () => {
      setMatches(mediaQueryList.matches);
    };
    handleChange();
    mediaQueryList.addEventListener('change', handleChange);
    return () => {
      mediaQueryList.removeEventListener('change', handleChange);
    };
  }, [query]);

  return matches;
}

/**
 * Settings sub-navigation and nested /settings/* routes.
 */
export const Settings: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isNarrow = useMediaQueryMatches('(max-width: 959.95px)');

  const permissions = usePermissions();
  const areas = useMemo(
    () => visibleAreas(id => hasPermission(permissions, id)),
    [permissions]
  );

  const activeArea =
    areas.find(area => location.pathname.startsWith(area.path))?.id ??
    DEFAULT_AREA;

  return (
    <div className="flex h-full flex-col">
      <PageHeader title="Settings" />
      <div className="flex-1 overflow-auto px-4 py-6 sm:px-6">
        <div className="mx-auto w-full max-w-[1280px]">
          {isNarrow ? (
            <nav
              aria-label="Settings"
              className="mb-6 flex gap-1 overflow-x-auto pb-1"
              data-testid="settings-nav"
            >
              {areas.map(area => (
                <button
                  key={area.id}
                  type="button"
                  onClick={() => navigate(area.path)}
                  data-testid={area.testId}
                  aria-current={area.id === activeArea ? 'page' : undefined}
                  className={`shrink-0 rounded-lg px-3 py-2 type-body-md outline-none transition-colors focus-visible:ring-2 focus-visible:ring-border-focused ${
                    area.id === activeArea
                      ? 'bg-surface-selected font-medium text-content-primary'
                      : 'text-content-secondary hover:bg-surface-raised hover:text-content-primary'
                  }`}
                >
                  {area.label}
                </button>
              ))}
            </nav>
          ) : (
            <div className="flex items-start gap-8">
              <nav
                aria-label="Settings"
                className="w-[220px] shrink-0"
                data-testid="settings-nav"
              >
                {areas.map(area => (
                  <button
                    key={area.id}
                    type="button"
                    onClick={() => navigate(area.path)}
                    data-testid={area.testId}
                    aria-current={area.id === activeArea ? 'page' : undefined}
                    className={`block w-full rounded-lg px-4 py-2.5 text-left type-body-md outline-none transition-colors focus-visible:ring-2 focus-visible:ring-border-focused ${
                      area.id === activeArea
                        ? 'bg-surface-selected font-medium text-content-primary'
                        : 'text-content-secondary hover:bg-surface-raised hover:text-content-primary'
                    }`}
                  >
                    {area.label}
                  </button>
                ))}
              </nav>
              <div className="min-w-0 grow">
                <SettingsRoutes />
              </div>
            </div>
          )}

          {isNarrow && <SettingsRoutes />}
        </div>
      </div>
    </div>
  );
};

const SettingsRoutes: React.FC = () => (
  <RRDRoutes>
    <Route index element={<Navigate to={DEFAULT_AREA} replace />} />
    <Route path="general" element={<GeneralArea />} />
    <Route path="organisation" element={<OrganisationArea />} />
    <Route path="auto-submit" element={<AutoSubmitArea />} />
    <Route path="late-submission" element={<LateSubmissionArea />} />
    <Route path="security" element={<SecurityArea />} />
    <Route path="*" element={<Navigate to={DEFAULT_AREA} replace />} />
  </RRDRoutes>
);
